'use client';

import clsx from 'clsx';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import React, { Children, HTMLAttributes, ReactNode, useLayoutEffect, useRef, useState } from 'react';

import ShinyText from '@/components/ui/ShinyText/ShinyText';
import styles from './Stepper.module.scss';

// -----------------------
// Shared Variables
// -----------------------

const stepVariants: Variants = {
    enter: (dir: number) => ({
        x: dir >= 0 ? '-100%' : '100%',
        opacity: 0,
    }),
    center: {
        x: '0%',
        opacity: 1,
    },
    exit: (dir: number) => ({
        x: dir >= 0 ? '50%' : '-50%',
        opacity: 0,
    }),
};

// -----------------------
// Helper Components
// -----------------------

interface SlideTransitionProps {
    children: ReactNode;
    direction: number;
    onHeightReady: (h: number) => void;
}

function SlideTransition({ children, direction, onHeightReady }: SlideTransitionProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        if (containerRef.current) {
            onHeightReady(containerRef.current.offsetHeight);
        }
    }, [children, onHeightReady]);

    return (
        <motion.div
            ref={containerRef}
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4 }}
            style={{ position: 'absolute', left: 0, right: 0, top: 0 }}
        >
            {children}
        </motion.div>
    );
}

interface StepContentWrapperProps {
    isCompleted: boolean;
    currentStep: number;
    direction: number;
    children: ReactNode;
    className?: string;
}

function StepContentWrapper({ isCompleted, currentStep, direction, children, className }: StepContentWrapperProps) {
    const [parentHeight, setParentHeight] = useState<number>(0);

    return (
        <motion.div
            className={className}
            style={{ position: 'relative', overflow: 'hidden' }}
            animate={{ height: isCompleted ? 0 : parentHeight }}
            transition={{ type: 'spring', duration: 0.4 }}
        >
            <AnimatePresence
                initial={false}
                mode="sync"
                custom={direction}
            >
                {!isCompleted && (
                    <SlideTransition
                        key={currentStep}
                        direction={direction}
                        onHeightReady={h => setParentHeight(h)}
                    >
                        {children}
                    </SlideTransition>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

interface CheckIconProps extends React.SVGProps<SVGSVGElement> {}

function CheckIcon(props: CheckIconProps) {
    return (
        <svg
            {...props}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            style={{
                color: 'white',
            }}
            viewBox="0 0 24 24"
        >
            <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.1, type: 'tween', ease: 'easeOut', duration: 0.3 }}
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
            />
        </svg>
    );
}

interface StepIndicatorProps {
    step: number;
    currentStep: number;
    onClickStep: (step: number) => void;
    disableStepIndicators?: boolean;
}

function StepIndicator({ step, currentStep, onClickStep, disableStepIndicators }: StepIndicatorProps) {
    let status: 'active' | 'inactive' | 'complete';
    if (currentStep === step) {
        status = 'active';
    } else if (currentStep < step) {
        status = 'inactive';
    } else {
        status = 'complete';
    }

    const handleClick = () => {
        if (step !== currentStep && !disableStepIndicators) {
            onClickStep(step);
        }
    };

    return (
        <motion.div
            onClick={handleClick}
            className={clsx(styles.stepIndicator)}
            animate={status}
            initial={false}
        >
            <motion.div
                variants={{
                    inactive: { scale: 1, backgroundColor: '#222', color: '#a3a3a3' },
                    active: { scale: 1, backgroundColor: '#cf9424', color: '#085a5c' },
                    complete: { scale: 1, backgroundColor: '#085a5c', color: '#ffff' },
                }}
                transition={{ duration: 0.3 }}
                className={clsx(styles.stepIndicatorInner)}
            >
                {(() => {
                    if (status === 'complete') {
                        return <CheckIcon className={clsx(styles.checkIcon)} />;
                    }
                    if (status === 'active') {
                        return <div className={clsx(styles.activeDot)} />;
                    }
                    return <span className={clsx(styles.stepNumber)}>{step}</span>;
                })()}
            </motion.div>
        </motion.div>
    );
}

interface StepConnectorProps {
    isComplete: boolean;
}

function StepConnector({ isComplete }: StepConnectorProps) {
    const lineVariants: Variants = {
        incomplete: { width: 0, backgroundColor: 'transparent' },
        complete: { width: '100%', backgroundColor: '#6aab87' },
    };

    return (
        <div className={clsx(styles.stepConnector)}>
            <motion.div
                className={clsx(styles.stepConnectorInner)}
                variants={lineVariants}
                initial={false}
                animate={isComplete ? 'complete' : 'incomplete'}
                transition={{ duration: 0.4 }}
            />
        </div>
    );
}

interface StepProps {
    children: ReactNode;
}

export function Step({ children }: StepProps): JSX.Element {
    return <div className={clsx(styles.stepDefault)}>{children}</div>;
}

// -----------------------
// Main Component
// -----------------------

interface StepperProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
    currentStep?: number;
    onStepChange?: (step: number) => void;
    onFinalStepCompleted?: () => void;
    stepCircleContainerClassName?: string;
    stepContainerClassName?: string;
    contentClassName?: string;
    footerClassName?: string;
    backButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
    nextButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
    backButtonText?: string;
    nextButtonText?: string;
    nextButtonFunction?: (step: number) => void;
    disableStepIndicators?: boolean;
    renderStepIndicator?: (props: RenderStepIndicatorProps) => ReactNode;
}

interface RenderStepIndicatorProps {
    step: number;
    currentStep: number;
    onStepClick: (clicked: number) => void;
}

export default function Stepper({
    children,
    currentStep = 1,
    onStepChange = () => {},
    onFinalStepCompleted = () => {},
    stepCircleContainerClassName = '',
    stepContainerClassName = '',
    contentClassName = '',
    footerClassName = '',
    backButtonProps = {},
    nextButtonProps = {},
    backButtonText = 'Back',
    nextButtonText = 'Continue',
    nextButtonFunction = () => {},
    disableStepIndicators = false,
    renderStepIndicator,
    ...rest
}: StepperProps) {
    const [direction, setDirection] = useState<number>(0);
    const stepsArray = Children.toArray(children);
    const totalSteps = stepsArray.length;
    const isCompleted = currentStep > totalSteps;
    const isLastStep = currentStep === totalSteps;

    const updateStep = (newStep: number) => {
        if (newStep > totalSteps) {
            onFinalStepCompleted();
        } else {
            onStepChange(newStep);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setDirection(-1);
            updateStep(currentStep - 1);
        }
    };

    const handleNext = () => {
        if (!isLastStep) {
            nextButtonFunction?.(currentStep);
            setDirection(1);
            updateStep(currentStep + 1);
        }
    };

    const handleComplete = () => {
        setDirection(1);
        updateStep(totalSteps + 1);
    };

    return (
        <div
            className={clsx(styles.outerContainer)}
            {...rest}
        >
            <div
                className={clsx(styles.stepCircleContainer, stepCircleContainerClassName)}
                style={{ border: '1px solid #222' }}
            >
                <div className={clsx(styles.stepIndicatorRow, stepContainerClassName)}>
                    {stepsArray.map((_, index) => {
                        const stepNumber = index + 1;
                        const isNotLastStep = index < totalSteps - 1;
                        return (
                            <React.Fragment key={stepNumber}>
                                {renderStepIndicator ? (
                                    renderStepIndicator({
                                        step: stepNumber,
                                        currentStep,
                                        onStepClick: clicked => {
                                            setDirection(clicked > currentStep ? 1 : -1);
                                            updateStep(clicked);
                                        },
                                    })
                                ) : (
                                    <StepIndicator
                                        step={stepNumber}
                                        disableStepIndicators={disableStepIndicators}
                                        currentStep={currentStep}
                                        onClickStep={clicked => {
                                            setDirection(clicked > currentStep ? 1 : -1);
                                            updateStep(clicked);
                                        }}
                                    />
                                )}
                                {isNotLastStep && <StepConnector isComplete={currentStep > stepNumber} />}
                            </React.Fragment>
                        );
                    })}
                </div>

                <StepContentWrapper
                    isCompleted={isCompleted}
                    currentStep={currentStep}
                    direction={direction}
                    className={clsx(styles.stepContentDefault, contentClassName)}
                >
                    {stepsArray[currentStep - 1]}
                </StepContentWrapper>

                {!isCompleted && (
                    <div className={clsx(styles.footerContainer, footerClassName)}>
                        <div
                            className={clsx(styles.footerNav, {
                                [styles.footerNavSpread]: currentStep !== 1,
                                [styles.footerNavEnd]: currentStep === 1,
                            })}
                        >
                            {currentStep !== 1 && backButtonText && (
                                <button
                                    onClick={handleBack}
                                    className={clsx(styles.backButton, {
                                        [styles.backButtonInactive]: currentStep === 1,
                                    })}
                                    {...backButtonProps}
                                >
                                    {backButtonText}
                                </button>
                            )}

                            {nextButtonText && (
                                <button
                                    onClick={isLastStep ? handleComplete : handleNext}
                                    className={clsx(styles.nextButton)}
                                    {...nextButtonProps}
                                >
                                    {isLastStep ? (
                                        'Complete'
                                    ) : (
                                        <ShinyText
                                            text={nextButtonText}
                                            disabled={false}
                                            speed={3}
                                        />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
